import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';

const BookMarketingStrategies = () => {
  return (
    <>
      <Helmet>
        <title>Book Marketing Strategies That Work in 2025 | Rushmore Publishing</title>
        <meta name="description" content="Learn cutting-edge book marketing techniques to boost sales and reach wider audiences in 2025." />
      </Helmet>

      <article className="min-h-screen bg-white pt-24 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 mb-8 font-semibold">
            <ArrowLeft className="w-5 h-5" /> Back to Blog
          </Link>

          <div className="mb-8">
            <span className="bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold">Marketing</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Book Marketing Strategies That Actually Work in 2025
          </h1>

          <div className="flex items-center gap-6 text-gray-600 mb-8 pb-8 border-b border-gray-200">
            <span className="flex items-center gap-2"><User className="w-5 h-5" />Emily Rodriguez</span>
            <span className="flex items-center gap-2"><Calendar className="w-5 h-5" />Jan 10, 2025</span>
            <span className="flex items-center gap-2"><Clock className="w-5 h-5" />6 min read</span>
          </div>

          <img src="https://images.unsplash.com/photo-1432821596592-e2c18b78144f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Book Marketing" className="w-full h-96 object-cover rounded-2xl mb-12" />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              In 2025, book marketing is more digital and data-driven than ever. Here are the strategies that are actually moving the needle for authors.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">1. Build Your Email List First</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Social media platforms come and go, but email is forever. Your email list is the most valuable marketing asset you can build. Offer a free chapter, short story, or exclusive content to encourage sign-ups.
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Use lead magnets (free content) to attract subscribers</li>
              <li>Send regular newsletters with value, not just sales pitches</li>
              <li>Segment your list for targeted messaging</li>
              <li>Aim for at least one email per month to stay top-of-mind</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">2. Leverage Amazon's Algorithm</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Amazon sells 80% of all ebooks. Understanding how their algorithm works is crucial:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Categories:</strong> Choose the right categories to rank in Top 100 lists</li>
              <li><strong>Keywords:</strong> Use all 7 keyword slots with targeted search terms</li>
              <li><strong>Reviews:</strong> Get honest reviews quickly after launch (minimum 50 is ideal)</li>
              <li><strong>Price Promotions:</strong> Use countdown deals and free promotions strategically</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">3. Master Social Media Marketing</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Focus on 1-2 platforms where your readers actually are:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>BookTok (TikTok):</strong> Short, engaging videos about your book or writing process</li>
              <li><strong>Instagram:</strong> Beautiful book photos, quotes, and behind-the-scenes content</li>
              <li><strong>Facebook Groups:</strong> Join reader groups in your genre and provide value</li>
              <li><strong>Twitter/X:</strong> Connect with other authors and engage in writing communities</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">4. Run Targeted Ads</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Paid advertising can significantly boost visibility:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li><strong>Amazon Ads:</strong> Start with $5-10/day, target similar books</li>
              <li><strong>BookBub Ads:</strong> Great for reaching serious readers</li>
              <li><strong>Facebook Ads:</strong> Use lookalike audiences based on your email list</li>
              <li><strong>Always test:</strong> Try different images, copy, and audiences</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">5. Collaborate with Book Bloggers and Reviewers</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Book bloggers and BookTubers can expose your book to thousands of targeted readers:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Research bloggers who review your genre</li>
              <li>Personalize your pitch - show you've read their content</li>
              <li>Offer ARCs (Advanced Reader Copies) 2-3 months before launch</li>
              <li>Make it easy: provide images, book description, and author bio</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">6. Create a Launch Strategy</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Your launch week is crucial for long-term success:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Build anticipation with a cover reveal 3 months out</li>
              <li>Organize a launch team of 20-50 readers for reviews</li>
              <li>Schedule social media posts across all platforms</li>
              <li>Consider a special launch price ($0.99 or $2.99)</li>
              <li>Line up podcast appearances and blog tours</li>
            </ul>

            <div className="bg-amber-50 border-l-4 border-amber-700 p-6 my-12 rounded-r-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Marketing Checklist</h3>
              <ul className="space-y-2 text-gray-700">
                <li>✓ Build email list with lead magnet</li>
                <li>✓ Optimize Amazon listing (keywords, categories, description)</li>
                <li>✓ Get 50+ reviews within first month</li>
                <li>✓ Be active on 1-2 social media platforms</li>
                <li>✓ Test paid advertising with small budget</li>
                <li>✓ Connect with book bloggers in your genre</li>
                <li>✓ Plan a strategic launch</li>
                <li>✓ Track what works and double down</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              Remember: Marketing is a marathon, not a sprint. Focus on building genuine connections with readers, and the sales will follow. Start implementing these strategies today and adjust based on what works for your specific audience.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-600 mb-6">Need help with your book marketing?</p>
            <Link to="/" className="inline-block">
              <button className="blob-btn">
                Explore Marketing Services
                <span className="blob-btn__inner">
                  <span className="blob-btn__blobs">
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                    <span className="blob-btn__blob"></span>
                  </span>
                </span>
              </button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default BookMarketingStrategies;